import React, { useEffect, useState } from "react";
import {
  Add,
  CalendarMonth,
  CheckBoxOutlineBlank,
  FilterList,
  Group,
  MoreHoriz,
  Sort,
  TableChart,
  Timeline,
  ViewKanban,
} from "@mui/icons-material";
import { getWorkspacePeople } from "../../../../services/workspaceApi";
import { WorkspaceUserAvatar, WorkspaceUserHoverCard } from "../../common/WorkspaceUserHoverCard";

const priorityClass = {
  High: "high",
  Medium: "medium",
  Low: "low",
};

const documentationPreviewRecords = [
  { id: "preview-1", title: "Employee Details", description: "Create a page where there is information about employees", due: "29 Nov, 2024", owner: "AM", assignees: ["NS", "RK"], priority: "Medium", status: "To Do", label: "People", category: "Feature" },
  { id: "preview-2", title: "Darkmode Version", description: "Darkmode version for all screens", due: "29 Nov, 2024", owner: "SK", assignees: ["AP", "VM"], priority: "Low", status: "To Do", label: "Design", category: "Task" },
  { id: "preview-3", title: "Super Admin Role", description: "-", due: "29 Nov, 2024", owner: "JD", assignees: ["KP", "TA"], priority: "Medium", status: "To Do", label: "Platform", category: "Feature" },
  { id: "preview-4", title: "Super Admin Role", description: "-", due: "29 Nov, 2024", owner: "AR", assignees: ["MK"], priority: "High", status: "In Progress", label: "Platform", category: "Task" },
  { id: "preview-5", title: "Settings Page", description: "-", due: "29 Nov, 2024", owner: "NS", assignees: ["RD", "VP"], priority: "Medium", status: "In Progress", label: "Design", category: "Task" },
  { id: "preview-6", title: "Performance Dashboard", description: "Create a design that displays KPIs and employee statistics", due: "29 Nov, 2024", owner: "PM", assignees: ["ZA"], priority: "Low", status: "In Progress", label: "Reports", category: "Feature" },
];

const priorityWeight = { High: 1, Medium: 2, Low: 3 };
const readLabels = (record) => Array.isArray(record.labels) ? record.labels : `${record.label || record.labels || ""}`.split(",").map((label) => label.trim()).filter(Boolean);
const readCategory = (record) => record.category || record.type || "Task";

function ProjectTabs({ view, setView }) {
  return (
    <div className="project-dashboard-tabs" aria-label="Project views">
      <button aria-selected={view === "table"} className={view === "table" ? "active" : ""} onClick={() => setView("table")} role="tab" type="button">
        <TableChart fontSize="small" />
        Table
      </button>
      <button aria-selected={view === "board"} className={view === "board" ? "active" : ""} onClick={() => setView("board")} role="tab" type="button">
        <ViewKanban fontSize="small" />
        Board
      </button>
      <button aria-selected={view === "timeline"} className={view === "timeline" ? "active" : ""} onClick={() => setView("timeline")} role="tab" type="button">
        <Timeline fontSize="small" />
        Timeline
      </button>
    </div>
  );
}

function PeopleStack({ people, profiles = {} }) {
  return (
    <div className="project-people-stack" aria-label="Assigned people">
      {people.map((person, index) => {
        const profile = profiles[String(person).toLowerCase()] || { name: person };
        return (
          <WorkspaceUserHoverCard className="project-person-hover" key={`${person}-${index}`} user={profile}>
            <WorkspaceUserAvatar user={profile} />
          </WorkspaceUserHoverCard>
        );
      })}
    </div>
  );
}

function ProgressCell({ value }) {
  return (
    <div className="project-progress-cell">
      <span>
        <i style={{ width: `${value}%` }} />
      </span>
      <small>{value}%</small>
    </div>
  );
}

function ProjectTaskRow({ task, profiles }) {
  return (
    <div className="project-task-row">
      <span className="project-check-cell">
        <CheckBoxOutlineBlank fontSize="small" />
      </span>
      <strong>{task.name}</strong>
      <p>{task.description}</p>
      <span className="project-deadline">{task.deadline}</span>
      <PeopleStack people={task.people} profiles={profiles} />
      <ProgressCell value={task.progress} />
      <span className={`project-priority ${priorityClass[task.priority] || ""}`}>{task.priority}</span>
      <button aria-label={`More actions for ${task.name}`} className="project-row-menu" type="button">
        <MoreHoriz fontSize="small" />
      </button>
    </div>
  );
}

function ProjectGroup({ group, onAddTask, profiles }) {
  return (
    <section className="project-task-group">
      <button className="project-group-header" onClick={onAddTask} type="button">
        <i style={{ background: group.color }} />
        <strong>{group.title}</strong>
        <em>{group.tasks.length}</em>
        <Add className="project-group-add" fontSize="small" />
      </button>
      <div className="project-task-table" role="table" aria-label={`${group.title} tasks`}>
        <div className="project-task-row project-task-head" role="row">
          <span />
          <span>Task Name</span>
          <span>Description</span>
          <span>
            <CalendarMonth fontSize="small" />
            Deadline
          </span>
          <span>
            <Group fontSize="small" />
            People
          </span>
          <span>Progress</span>
          <span>Priority</span>
          <span />
        </div>
        {group.tasks.map((task) => (
          <ProjectTaskRow key={`${group.title}-${task.name}-${task.progress}`} profiles={profiles} task={task} />
        ))}
      </div>
    </section>
  );
}

export function ProjectsView({ projectName = "", records, onAddTask = () => {} }) {
  const [view, setView] = useState("table");
  const [sortBy, setSortBy] = useState("name");
  const [labelFilter, setLabelFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [profiles, setProfiles] = useState({});
  useEffect(() => { getWorkspacePeople().then((data) => setProfiles(Object.fromEntries((data.people || []).map((person) => [String(person.name || "").toLowerCase(), person])))).catch(() => setProfiles({})); }, []);
  const isDocumentationPreview = !Array.isArray(records);
  const sourceRecords = isDocumentationPreview ? documentationPreviewRecords : records;
  const labels = Array.from(new Set(sourceRecords.flatMap(readLabels))).sort();
  const categories = Array.from(new Set(sourceRecords.map(readCategory))).sort();
  const statuses = Array.from(new Set(sourceRecords.map((record) => record.status).filter(Boolean))).sort();
  const filteredRecords = sourceRecords
    .filter((record) => !labelFilter || readLabels(record).includes(labelFilter))
    .filter((record) => !categoryFilter || readCategory(record) === categoryFilter)
    .filter((record) => !statusFilter || record.status === statusFilter)
    .slice()
    .sort((left, right) => {
      if (sortBy === "priority") return (priorityWeight[left.priority] || 9) - (priorityWeight[right.priority] || 9);
      if (sortBy === "due") return `${left.due || "9999"}`.localeCompare(`${right.due || "9999"}`);
      return `${left.title || ""}`.localeCompare(`${right.title || ""}`);
    });
  const dynamicGroups = [
    { title: "To-do", color: "#22c55e", tasks: filteredRecords.filter((record) => /idea|new|backlog|to do|planned|ready/i.test(record.status)).map((record) => ({ ...record, name: record.title, deadline: record.due || "No deadline", people: [record.owner, ...(record.assignees || [])].filter(Boolean), progress: 0 })) },
    { title: "In Progress", color: "#facc15", tasks: filteredRecords.filter((record) => /in progress|active|qa/i.test(record.status)).map((record) => ({ ...record, name: record.title, deadline: record.due || "No deadline", people: [record.owner, ...(record.assignees || [])].filter(Boolean), progress: 50 })) },
    { title: "In Review", color: "#8b5cf6", tasks: filteredRecords.filter((record) => /review|approval|waiting/i.test(record.status)).map((record) => ({ ...record, name: record.title, deadline: record.due || "No deadline", people: [record.owner, ...(record.assignees || [])].filter(Boolean), progress: 80 })) },
    { title: "Done", color: "#14b8a6", tasks: filteredRecords.filter((record) => /done|completed|resolved|closed/i.test(record.status)).map((record) => ({ ...record, name: record.title, deadline: record.due || "No deadline", people: [record.owner, ...(record.assignees || [])].filter(Boolean), progress: 100 })) },
  ];
  const groups = dynamicGroups;
  const completed = sourceRecords.length ? dynamicGroups.find((group) => group.title === "Done").tasks.length : 0;
  const completion = isDocumentationPreview ? 50 : (sourceRecords.length ? Math.round((completed / sourceRecords.length) * 100) : 0);
  const members = Array.from(new Set(filteredRecords.flatMap((record) => [record.owner, ...(record.assignees || [])]).filter(Boolean))).slice(0, 4);
  return (
    <section className="project-progress-dashboard">
      <header className="project-dashboard-header">
        <div className="project-dashboard-title">
          <span className="project-dashboard-icon">
            <TableChart fontSize="small" />
          </span>
          <div>
            <h2>{isDocumentationPreview ? "Project Progress Dashboard" : `${projectName || "Project"} progress`}</h2>
            <div className="project-dashboard-progress">
              <span>
                <i style={{ width: `${completion}%` }} />
              </span>
              <small>{completion}% Completed</small>
            </div>
          </div>
        </div>
        <div className="project-dashboard-members">
          <PeopleStack people={members.length ? members : ["—"]} profiles={profiles} />
          {members.length > 4 ? <span className="project-more-members">{members.length}+</span> : null}
          <button onClick={onAddTask} type="button">
            <Add fontSize="small" />
            Add Member
          </button>
        </div>
      </header>

      <div className="project-dashboard-toolbar">
        <ProjectTabs setView={setView} view={view} />
        <div className="project-dashboard-actions">
          <button onClick={() => setSortBy((current) => current === "name" ? "priority" : current === "priority" ? "due" : "name")} type="button">
            <Sort fontSize="small" />
            Sort: {sortBy === "name" ? "Name" : sortBy === "priority" ? "Priority" : "Due"}
          </button>
          <label className="project-dashboard-select">Label<select aria-label="Filter by label" onChange={(event) => setLabelFilter(event.target.value)} value={labelFilter}><option value="">All labels</option>{labels.map((label) => <option key={label} value={label}>{label}</option>)}</select></label>
          <label className="project-dashboard-select">Category<select aria-label="Filter by category" onChange={(event) => setCategoryFilter(event.target.value)} value={categoryFilter}><option value="">All categories</option>{categories.map((category) => <option key={category} value={category}>{category}</option>)}</select></label>
          <label className="project-dashboard-select project-dashboard-filter"><FilterList fontSize="small" /><span>Filters</span><select aria-label="Filter by status" onChange={(event) => setStatusFilter(event.target.value)} value={statusFilter}><option value="">All statuses</option>{statuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
          {(labelFilter || categoryFilter || statusFilter) ? <button className="project-dashboard-clear" onClick={() => { setLabelFilter(""); setCategoryFilter(""); setStatusFilter(""); }} type="button">Clear</button> : null}
          <button className="primary" onClick={onAddTask} type="button">
            <Add fontSize="small" />
            Add Task
          </button>
        </div>
      </div>

      {view === "table" ? <div className="project-task-groups">{groups.map((group) => <ProjectGroup group={group} key={group.title} onAddTask={onAddTask} profiles={profiles} />)}</div> : null}
      {view === "board" ? <div className="functional-kanban project-dashboard-board" aria-label="Project board">{groups.map((group) => <section key={group.title}><header><strong>{group.title}</strong><span>{group.tasks.length}</span></header><div>{group.tasks.map((task) => <article key={`${group.title}-${task.id || task.name}`}><strong>{task.name}</strong><span>{task.description || "No description"}</span><small>{task.deadline} · {task.priority}</small></article>)}{!group.tasks.length ? <p>No tasks</p> : null}</div></section>)}</div> : null}
      {view === "timeline" ? <div className="project-timeline-list" aria-label="Project timeline">{filteredRecords.slice().sort((left, right) => `${left.due || "9999"}`.localeCompare(`${right.due || "9999"}`)).map((task) => <article key={task.id}><time>{task.due || "No deadline"}</time><div><strong>{task.title}</strong><span>{task.status} · {task.priority}</span></div></article>)}{!filteredRecords.length ? <p className="empty-state">No scheduled work matches these filters.</p> : null}</div> : null}
    </section>
  );
}
